import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Gauge, Loader2, Monitor, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { errMsg, formatDate, useApi } from "@/lib/api";
import { CATEGORY, ScoreRing, ms } from "./qualityUi";

const PHASE = { start: "avvio", mobile: "telefono", desktop: "computer" };

function Result({ preset, r }) {
  const Icon = preset === "mobile" ? Smartphone : Monitor;
  const failed = Object.entries(r.failed || {}).flatMap(([c, l]) => l.map((x) => ({ ...x, c })));
  return (
    <div className="rounded-lg border border-slate-200 p-3" data-testid={`lh-result-${preset}`}>
      <p className="flex items-center gap-1.5 text-xs font-semibold text-slate-700"><Icon size={13} />{preset === "mobile" ? "Telefono" : "Computer"}</p>
      <div className="mt-2 grid grid-cols-2 justify-items-center gap-3 sm:grid-cols-4 sm:justify-items-start">
        {["performance", "accessibility", "best-practices", "seo"].map((c) => (
          <div key={c} className="flex flex-col items-center gap-1"><ScoreRing value={r.scores?.[c]} size={44} testid={`lh-${preset}-${c}`} /><span className="text-[10px] text-slate-500">{CATEGORY[c]}</span></div>
        ))}
      </div>
      <p className="mt-2 text-[11px] text-slate-600 tabular-nums">LCP {ms(r.lcp_ms)} · FCP {ms(r.fcp_ms)} · TBT {ms(r.tbt_ms)} · CLS {r.cls == null ? "—" : String(r.cls).replace(".", ",")} · {Math.round(r.bytes_kb || 0)} KB · {r.requests} richieste</p>
      {failed.length > 0 && <p className="mt-1 text-[11px] text-slate-500">Da migliorare: {failed.slice(0, 6).map((f) => f.title.replace(/`/g, "")).join(" · ")}</p>}
    </div>
  );
}

function Audit({ a }) {
  return (
    <li className="py-3" data-testid={`lh-audit-${a.id}`}>
      <p className="flex flex-wrap items-center gap-x-2 text-xs text-slate-600"><b className="text-slate-800">{a.target === "live" ? "Online" : "Anteprima"}</b>
        <a href={a.url} target="_blank" rel="noreferrer" className="break-all text-sky-700 hover:underline">{a.url}</a>{a.version && <span>· {a.version}</span>}<span>· {formatDate(a.started_at, true)}</span></p>
      {a.status === "running" && <p className="mt-2 flex items-center gap-2 text-xs text-sky-700" data-testid="lh-running"><Loader2 size={13} className="animate-spin" />Misura in corso ({PHASE[a.phase] || a.phase})…</p>}
      {a.status === "failed" && <p className="mt-2 text-xs text-red-700" data-testid="lh-error">Misura non riuscita: {a.error}</p>}
      {a.noindex && <p className="mt-1 text-[11px] text-amber-700">Indirizzo di anteprima (noindex): la SEO reale si misura sull'indirizzo online.</p>}
      <div className="mt-2 grid gap-2 lg:grid-cols-2">{["mobile", "desktop"].filter((p) => a.results?.[p]).map((p) => <Result key={p} preset={p} r={a.results[p]} />)}</div>
    </li>
  );
}

export default function LighthousePanel({ site, routes }) {
  const api = useApi();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [target, setTarget] = useState(site.urls?.live ? "live" : "preview");
  const [path, setPath] = useState("/");
  const load = useCallback(() => api.get(`/sites/${site.id}/lighthouse`).then(({ data: d }) => { setData(d); setLoadError(""); })
    .catch((e) => setLoadError(errMsg(e))), [api, site.id]);
  useEffect(() => { load(); }, [load]);
  const running = data?.audits?.some((a) => a.status === "running");
  // also while another site is being measured: the button unlocks by itself when that measurement ends
  const waiting = running || data?.busy_elsewhere;
  useEffect(() => { if (!waiting) return undefined; const t = setInterval(load, 3000); return () => clearInterval(t); }, [waiting, load]);
  const start = async () => {
    try { await api.post(`/sites/${site.id}/lighthouse`, { target, path }); toast.success("Misura Lighthouse avviata: circa 1 minuto"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!data) {
    return loadError ? <p className="text-xs text-red-700" data-testid="lh-load-error">Impossibile caricare le misure Lighthouse: {loadError} <button type="button" className="underline" onClick={load} data-testid="lh-load-retry">Riprova</button></p>
      : <Loader2 size={14} className="animate-spin text-slate-400" />;
  }
  const targets = [["live", "Online"], ["preview", "Anteprima"]].filter(([k]) => site.urls?.[k]);
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="lighthouse-panel">
      <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-900"><Gauge size={14} className="text-sky-600" />Misura reale con Lighthouse</p>
      <p className="mt-1 text-xs text-slate-500">{data.message} Una misura singola può variare di qualche punto: per confronti usa la stessa pagina e lo stesso indirizzo.</p>
      {data.available && targets.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Select value={target} onValueChange={setTarget}><SelectTrigger className="h-9 w-36" data-testid="lh-target"><SelectValue /></SelectTrigger>
            <SelectContent>{targets.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>
          <Select value={path} onValueChange={setPath}><SelectTrigger className="h-9 w-64 max-w-full" data-testid="lh-path"><SelectValue /></SelectTrigger>
            <SelectContent>{(routes?.length ? routes : [{ path: "/" }]).slice(0, 40).map((r) => <SelectItem key={r.path} value={r.path}>{r.path}</SelectItem>)}</SelectContent></Select>
          <Button size="sm" onClick={start} disabled={running || data.busy_elsewhere} className="bg-sky-600 hover:bg-sky-700" data-testid="lh-start-btn">
            {running ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Gauge size={14} className="mr-1.5" />}Misura con Lighthouse</Button>
          {data.busy_elsewhere && <span className="text-xs text-amber-700">Un'altra misura è in corso su un altro sito.</span>}
        </div>
      )}
      {data.available && !targets.length && <p className="mt-3 text-xs text-slate-500">Compila il sito (anteprima) o pubblicalo per poterlo misurare.</p>}
      {data.audits?.length > 0 ? <ul className="mt-2 divide-y divide-slate-100" data-testid="lh-audits">{data.audits.map((a) => <Audit key={a.id} a={a} />)}</ul>
        : <p className="mt-3 text-xs text-slate-400" data-testid="lh-empty">Nessuna misura ancora.</p>}
    </div>
  );
}
